// src/App.js
import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import axios from "axios";

// Auth Components
import Login from "./components/auth/Login";
import Register from "./components/auth/Register";
import SuperuserRegistration from "./components/auth/SuperUserRegistration"; // Import the new component

// Employee Components
import EmployeeDashboard from "./components/employee/Dashboard";
import JobDetails from "./components/employee/JobDetails";
import CaptureEquipment from "./components/employee/CaptureEquipment";

// Admin Components
import AdminDashboard from "./components/admin/Dashboard";
import JobForm from "./components/admin/JobForm";
import JobDetailsView from "./components/admin/JobDetailsView";

// Superuser Components
import SuperuserDashboard from "./components/superuser/Dashboard"; // Import the superuser dashboard
import CompanyForm from "./components/superuser/CompanyForm"; // You'll need to create this
import CompanyDetails from "./components/superuser/CompanyDetails"; // You'll need to create this

// Context for Authentication
import { AuthProvider } from "./context/AuthContext";

// Protected Route Component - Updated to handle superuser role
const ProtectedRoute = ({ children, requiredRole }) => {
  const token = localStorage.getItem("token");
  const userRole = localStorage.getItem("role");

  if (!token) {
    return <Navigate to="/login" />;
  }

  if (requiredRole && userRole !== requiredRole) {
    // Allow superusers to access admin routes if needed
    if (requiredRole === "admin" && userRole === "superuser") {
      return children;
    }

    // Redirect based on role
    if (userRole === "superuser") {
      return <Navigate to="/superuser/dashboard" />;
    } else if (userRole === "admin") {
      return <Navigate to="/admin" />;
    } else {
      return <Navigate to="/" />;
    }
  }

  return children;
};

function App() {
  // Set up axios defaults
  axios.defaults.baseURL =
    process.env.REACT_APP_API_URL || "https://app.fieldtag.ai/api";

  axios.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem("token");
      if (token) {
        config.headers["Authorization"] = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  return (
    <AuthProvider>
      <Router>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />

          {/* Special route for superuser registration - you might want to secure this */}
          <Route path="/setup-superuser" element={<SuperuserRegistration />} />

          {/* Employee Routes */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <EmployeeDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/job/:id"
            element={
              <ProtectedRoute>
                <JobDetails />
              </ProtectedRoute>
            }
          />
          <Route
            path="/job/:id/capture"
            element={
              <ProtectedRoute>
                <CaptureEquipment />
              </ProtectedRoute>
            }
          />

          {/* Admin Routes */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/job/new"
            element={
              <ProtectedRoute requiredRole="admin">
                <JobForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/job/:id"
            element={
              <ProtectedRoute requiredRole="admin">
                <JobForm />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/job/:id/view"
            element={
              <ProtectedRoute requiredRole="admin">
                <JobDetailsView />
              </ProtectedRoute>
            }
          />

          {/* Superuser Routes */}
          <Route
            path="/superuser/dashboard"
            element={
              <ProtectedRoute requiredRole="superuser">
                <SuperuserDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/superuser/company/new"
            element={
              <ProtectedRoute requiredRole="superuser">
                <CompanyForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/superuser/company/:id"
            element={
              <ProtectedRoute requiredRole="superuser">
                <CompanyDetails />
              </ProtectedRoute>
            }
          />
          <Route
            path="/superuser/company/:id/edit"
            element={
              <ProtectedRoute requiredRole="superuser">
                <CompanyForm />
              </ProtectedRoute>
            }
          />

          {/* Redirect for unknown routes */}
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
